import './Purchase.scss';

import { type FC, useState, useEffect, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  FontLicenseType,
  FontLicenseOptionsQuery,
} from 'client/graphql/types/operations';
import * as Core from 'components/core';
import FamiliesLicenseOptionSelect from './LicenseOptionSelect';
import PurchaseTable from './purchaseTable/PurchaseTable';
import LicenseConcepts from './purchaseTable/LicenseConcepts';

interface Props {
  family: {
    id: number;
    name: string;
    conceptFont: boolean;
    completePrice: number;
    fontFacePrice: number;
    subPackageFontFacePrice: number;
    discountedFontFacePrice: number;
    hasWidthVariableFiles: boolean;
    faces: {
      id: number;
      width: { slug: string; name: string; position: number };
      size: { slug: string; name: string; position: number };
      weight: { slug: string; name: string; position: number };
      style: { slug: string; name: string; position: number };
      description: string;
      familyPreview: { url: string | null };
      singlePreview: { url: string | null };
      singleCharacterPreview: { url: string | null };
      family: { fontFacePrice: number; discountedFontFacePrice: number };
    }[];
  };
}

const FONT_LICENSE_OPTIONS_QUERY = gql`
  query FontLicenseOptionsQuery {
    fontLicenseOptions {
      id
      type
      minValue
      maxValue
      multiplier
      description
    }
  }
`;

const FamiliesPurchase: FC<Props> = (props) => {
  const { family } = props;

  const { t } = useTranslation();

  const [licenseOptionId, setLicenseOptionId] = useState<number | null>(null);

  const { data, loading } = useQuery<FontLicenseOptionsQuery>(
    FONT_LICENSE_OPTIONS_QUERY,
  );

  useEffect(() => {
    if (data != null) {
      // TODO: Refactor to avoid setting state in useEffect
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLicenseOptionId(
        data.fontLicenseOptions.find(
          (flo) =>
            flo.type ===
            (family.conceptFont
              ? FontLicenseType.Concept
              : FontLicenseType.Desktop),
        )?.id ?? null,
      );
    }
  }, [data, family.conceptFont]);

  const licenseOption = useMemo(() => {
    if (data == null) {
      return null;
    }

    return (
      data.fontLicenseOptions.find((flo) => flo.id === licenseOptionId) ?? null
    );
  }, [data, licenseOptionId]);

  if (loading) {
    return null;
  }

  if (data == null) {
    throw new Error('Missing font license options data');
  }

  return (
    <>
      {family.conceptFont ? (
        <LicenseConcepts family={family} licenseOption={licenseOption} />
      ) : (
        <>
          <FamiliesLicenseOptionSelect
            options={data.fontLicenseOptions}
            value={licenseOptionId}
            onChange={setLicenseOptionId}
          />

          <PurchaseTable family={family} licenseOption={licenseOption} />
        </>
      )}

      {!family.conceptFont && (
        <div className="FamiliesPurchase-vatDisclaimer">
          {t('family.vatDisclaimer')}
        </div>
      )}

      <Core.ScrollToElement />
    </>
  );
};

export default FamiliesPurchase;
